import { useEffect, useRef } from 'react'
import { getGlobalCompactViewMode, isNullUndefined, setGlobalCompactViewMode } from '../../../common/Helpers';
import { geMessageById, getMessageTreeByObjectByParty } from '../../../services/sBook/message.service'
import { getExportBookPdfMessages, getMessageComments } from '../../../services/sBook/message-log.service';
import { cloneDeep } from 'lodash-es';

const useGetMessageById = (
    messageId,
    sectionPartyMemberId,
    setMessage,
    fireGetMessage,
    setFireGetMessage,
    setLoadFired
) => {

    /*
     * Última combinación messageId / sectionPartyMemberId
     * que ya inició una carga.
     */
    const lastLoadedKeyRef = useRef(null);

    /*
     * Evita lanzar dos llamadas simultáneas para
     * exactamente el mismo mensaje.
     */
    const loadingKeyRef = useRef(null);

    /*
     * Identifica qué mensaje está seleccionado actualmente.
     * Sirve para ignorar respuestas antiguas.
     */
    const currentKeyRef = useRef(null);

    useEffect(() => {

        if (
            messageId === null ||
            messageId === undefined ||
            messageId === 0
        ) {
            currentKeyRef.current = null;
            return;
        }

        const normalizedSectionPartyMemberId =
            sectionPartyMemberId === "#NA#"
                ? 0
                : sectionPartyMemberId;

        const requestKey =
            String(messageId) + "|" + String(normalizedSectionPartyMemberId);

        currentKeyRef.current = requestKey;

        const messageChanged =
            lastLoadedKeyRef.current !== requestKey;

        const forceUpdate =
            fireGetMessage === true;

        /*
         * Hacemos GET solamente cuando:
         *
         * 1. Cambió messageId o sectionPartyMemberId.
         * 2. fireGetMessage viene explícitamente en true.
         *
         * El objeto message/objMessage ya NO participa
         * en la decisión ni en las dependencias.
         */
        if (!messageChanged && !forceUpdate) {
            return;
        }

        /*
         * Si ya existe una llamada en curso para esta misma
         * combinación, no lanzamos otra.
         */
        if (loadingKeyRef.current === requestKey) {
            return;
        }

        /*
         * Se marca ANTES del await.
         * Así un render intermedio no dispara otro GET.
         */
        lastLoadedKeyRef.current = requestKey;
        loadingKeyRef.current = requestKey;

        const getData = async () => {
            try {

                const serviceResponse = await geMessageById(
                    messageId,
                    normalizedSectionPartyMemberId
                );

                /*
                 * Si mientras esperábamos cambió el mensaje,
                 * esta respuesta ya quedó obsoleta.
                 */
                if (currentKeyRef.current !== requestKey) {
                    return;
                }

                const response = serviceResponse;

                setMessage(response.data.data);

                if (setLoadFired) {
                    setLoadFired(true);
                }
            }
            catch (error) {

                if (currentKeyRef.current === requestKey) {
                    if (setLoadFired) {
                        setLoadFired(true);
                    }
                }
            }
            finally {

                if (loadingKeyRef.current === requestKey) {
                    loadingKeyRef.current = null;
                }

                /*
                 * Apagamos el trigger externo al terminar.
                 * El cambio true -> false hará reevaluar el efecto,
                 * pero NO volverá a llamar al API porque
                 * messageChanged=false y forceUpdate=false.
                 */
                if (
                    currentKeyRef.current === requestKey &&
                    setFireGetMessage
                ) {
                    setFireGetMessage(false);
                }
            }
        };

        getData();

    }, [
        messageId,
        sectionPartyMemberId,
        fireGetMessage
    ]);
};

const useGetMessageTreeByObjectByParty = (bookId, partyId, partyMemberId, messageTree, setMessageTree, setAuxMessageObject, messageQsId, auxMessageObjectId, setSelectedMessageId, setMessageTreeTypeList, setObjSectionList, setObjMessageListBase) => {    
    useEffect(()=> {
        const buildConcat = (msg) => {
            return Object.entries(msg)
                .filter(([_, v]) => v !== null && v !== undefined && v !== "")
                .filter(([_, v]) => typeof v !== "object") // evita [object Object] si hubiera anidados
                .map(([_, v]) => String(v))
                .join(" ");
        };

        const getData = async () => {
            try {
                let serviceResponse = await getMessageTreeByObjectByParty(bookId, partyId, partyMemberId);
                serviceResponse.data.data = serviceResponse.data.data.map(sec => ({
                    ...sec,
                    l_items: (sec.l_items ?? []).map(it => ({
                        ...it,
                        item_message: it.item_message
                        ? { ...it.item_message, fullTextProps: buildConcat(it.item_message) }
                        : it.item_message
                    }))
                }));
                
                const response = serviceResponse;

                response.data.data.forEach(section => {
                    section.show = true;
                });

                setMessageTree(response);
                setObjMessageListBase && setObjMessageListBase(response)

                let elementMaxDate = null;

                if (response.data.data.length > 0) {
                    const dataSection = response.data.data;
                    const listOfSections = []

                    dataSection.forEach(section => {
                        section.l_items.forEach(message => {
                            if (messageQsId !== null && messageQsId !== undefined) {
                                if (parseInt(messageQsId) === message.item_message.id) {
                                    elementMaxDate = message.item_message;
                                    elementMaxDate.fronPropertySectionName = section.seccionName;
                                    message.item_message.isUnread = false;
                                }
                            }
                            else {
                                if (elementMaxDate === null) {
                                    elementMaxDate = message.item_message;
                                    elementMaxDate.fronPropertySectionName = section.seccionName;
                                    message.item_message.isUnread = false;
                                }
                                else {
                                    if (elementMaxDate.id < message.item_message.id) {
                                        elementMaxDate = message.item_message;
                                        elementMaxDate.fronPropertySectionName = section.seccionName;
                                        message.item_message.isUnread = false;
                                    }
                                }
                            }
                        });
                        listOfSections.push(section);
                    });

                    if (auxMessageObjectId === null) {
                        setAuxMessageObject(elementMaxDate);
                        setSelectedMessageId(elementMaxDate.id)
                    }

                    setObjSectionList(listOfSections);

                    const listOfMessages = []
                    response.data.data.forEach(section => {
                        section.l_items.forEach(element => {
                            element.item_message.seccionName = section.seccionName;
                            element.item_message.l_items = section.l_items;
                            listOfMessages.push(element.item_message);
                        });
                    });
    
                    setMessageTreeTypeList(prevState => ({
                        ...prevState,
                        data: listOfMessages
                    }));
                }
            }
            catch (error) {
            }
        }
        
        if ((bookId !== null && bookId !== undefined) &&  (partyId !== null && partyId !== undefined) && (partyMemberId !== null && partyMemberId !== undefined) && messageTree === null) {
            getData()
        }
    }, [bookId, partyId, partyMemberId, messageTree, setMessageTree, setAuxMessageObject, messageQsId, setSelectedMessageId, setMessageTreeTypeList, auxMessageObjectId, setObjSectionList]);
}

const useOnFilterMessages = (messageListBase, filterText, forceFilter, setForceFilter, setMessageTree, setAuxMessageObject, setSelectedMessageId, setMessageTreeTypeList, setObjSectionList) => {    
    useEffect(() => {
        if (!forceFilter) return;

        const normalizeForSearch = (s) =>
            String(s ?? "")
            .toLowerCase()
            .normalize("NFD")
            .replace(/\p{Diacritic}/gu, "")
            .trim();

        const toDateSafe = (v) => {
            if (!v) return null;
            if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
            const d = new Date(v); // soporta ISO, timestamps (num/string), etc.
            return Number.isNaN(d.getTime()) ? null : d;
        };

        const dateTokensForSearch = (dateValue) => {
            const d = toDateSafe(dateValue);
            if (!d) return "";

            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");

            return [
            `${yyyy}${mm}${dd}`,   // 20250115
            `${yyyy}${mm}`,        // 202501
            `${yyyy}-${mm}-${dd}`, // 2025-01-15
            `${yyyy}-${mm}`,       // 2025-01
            `${dd}/${mm}/${yyyy}`, // 15/01/2025
            `${mm}/${yyyy}`,       // 01/2025
            ].join(" ");
        };

        const filterDataByText = (dataEnriched, text) => {
            const q = normalizeForSearch(text);
            if (!q) return dataEnriched ?? [];

            return (dataEnriched ?? [])
            .map((sec) => ({
                ...sec,
                l_items: (sec.l_items ?? []).filter((it) => {
                const msg = it?.item_message;

                const full = normalizeForSearch(msg?.fullTextProps);

                // CAMBIA "dateProp" por la propiedad real (ej: createdAt, deadlineDate, etc.)
                const datePart = normalizeForSearch(dateTokensForSearch(msg?.messageDate));

                const haystack = `${full} ${datePart}`;
                return haystack.includes(q);
                }),
            }))
            .filter((sec) => (sec.l_items ?? []).length > 0);
        };

        try {
            const filtredData = cloneDeep(messageListBase);

            if (!filtredData?.data?.data) {
            setMessageTree(filtredData);
            return;
            }

            filtredData.data.data = filterDataByText(filtredData.data.data, filterText);
            setMessageTree(filtredData);
        } catch (error) {
            // console.error(error);
        } finally {
            setForceFilter(false);
        }
    }, [forceFilter, filterText, messageListBase, setForceFilter, setMessageTree]);
}

const useAssignMessageToObject = (messageObjectToSet, messageObjectSetter, setMessageObject, isPanelOpen, forceSetMessageObject = false, setForceSetMessageObject) => {    
    useEffect(()=> {
        const setData = async () => {
            try {
                setMessageObject(messageObjectSetter);
                setForceSetMessageObject && setForceSetMessageObject(false)
            }
            catch (error) {
                setForceSetMessageObject && setForceSetMessageObject(false)
            }
        }
        
        if ((messageObjectToSet === null || forceSetMessageObject === true) && isPanelOpen) {
            setData()
        }
    }, [messageObjectToSet, messageObjectSetter, setMessageObject, isPanelOpen, forceSetMessageObject, setForceSetMessageObject]);
}

const useOfGetCompactViewMode = (componentName, compactViewMode, setCompactModeView, defaultValueOnNull) => {    
    useEffect(()=> {
        const getData = async () => {
            try {
                let viewMode = getGlobalCompactViewMode(componentName);

                if (viewMode === null || viewMode === undefined) {
                    setGlobalCompactViewMode(componentName, defaultValueOnNull);
                    viewMode = defaultValueOnNull;
                }

                setCompactModeView(viewMode === "true" || viewMode === true ? true : false);
            }
            catch (error) {
            }
        }
        
        if (componentName && compactViewMode === null) {
            getData()
        }
    }, [componentName, setCompactModeView, compactViewMode, defaultValueOnNull]);
}

const useOfBookPdfMessages = (bookId, partyMemberId, messageId, dataToExport, setDataToExport, setItems) => {
    useEffect(()=> {
        const getData = async () => {
            try {
                let serviceResponse = await getExportBookPdfMessages(bookId, partyMemberId, messageId);

                const response = serviceResponse.data;
                if (response.succeeded) {
                    setDataToExport(response.data);
                    setItems(response.data.l_sections?.flatMap(sec => sec.l_items) ?? [])
                }
                else {
                    setDataToExport(null);
                    setItems(null);
                }
            }
            catch (error) {
                setDataToExport(null);
                setItems(null);
            }
        }
        
        if (!isNullUndefined(bookId) && !isNullUndefined(partyMemberId) && dataToExport === undefined) {
            getData()
        }
    }, [bookId, partyMemberId, messageId, dataToExport, setDataToExport, setItems]);
}

const useOfBookMessageComments = (bookId, messageId, needGet, items, setItems) => {
    useEffect(()=> {
        const getData = async () => {
            try {
                let serviceResponse = await getMessageComments(bookId, messageId);

                const response = serviceResponse.data;
                if (response.succeeded) {
                    setItems(response.data)
                }
                else {
                    setItems(null);
                }
            }
            catch (error) {
                setItems(null);
            }
        }
        
        if (!isNullUndefined(bookId) && !isNullUndefined(messageId) && items === null && needGet === true) {
            getData()
        }
    }, [bookId, messageId, needGet, items, setItems]);
}

export {
    useGetMessageById,
    useGetMessageTreeByObjectByParty,
    useAssignMessageToObject,
    useOfGetCompactViewMode,
    useOfBookPdfMessages,
    useOnFilterMessages,
    useOfBookMessageComments
}